/**
 * useMandateStatusWatcher
 *
 * Keeps the eMandate page's UPI / NACH cards in sync with the SERVER's view of
 * each mandate, so the user never has to reload the page to see that their
 * mandate went through.
 *
 * ── The problem this solves ──────────────────────────────────────────────────
 * Mandate registration always leaves our tab:
 *   * UPI autopay  → the customer approves in their UPI / banking app
 *   * NACH / eNACH → the customer authenticates on the bank's Digio page
 *
 * Two things then routinely go wrong, and BOTH end with a mandate that is live
 * at the bank while the card on screen still says "pending":
 *
 *   1. Mobile OSes evict background tabs. When the customer switches to their
 *      UPI app, our tab can be frozen or discarded — which kills the in-card
 *      4-second poll loop. On return the loop is gone and never restarts.
 *   2. UPI confirms ASYNCHRONOUSLY. Digio reports 'partial' (user approved, NPCI
 *      not yet done, typically 1-2 min) and only later flips to
 *      register_success. A tab closed inside that window misses the transition.
 *
 * The server side is already handled: `los_app.poll_pending_mandates_task`
 * (Celery beat, every minute) polls Digio and writes status=4 regardless of
 * whether the browser is alive. The remaining gap is purely presentational — the
 * DB is correct but the UI does not know. This hook closes that gap.
 *
 * ── Why not a Web Worker ─────────────────────────────────────────────────────
 * A Web Worker does NOT help here, and it is worth being explicit about why:
 * workers live inside the page. When the OS freezes or discards the tab, the
 * worker is frozen or discarded with it — the exact failure we are trying to
 * survive. A worker also cannot re-render React or hold state across a tab
 * reload. The only thing that can be trusted to keep watching while the user is
 * away is the server (already done), so the browser's job is simply to ASK AGAIN
 * at the right moments — which is what this hook does.
 *
 * ── Strategy ─────────────────────────────────────────────────────────────────
 *   * Poll on an interval while the tab is visible.
 *   * STOP polling when the tab is hidden (a frozen timer is unreliable anyway,
 *     and firing requests from a background tab wastes battery and quota).
 *   * Re-fetch IMMEDIATELY on `visibilitychange` → visible, and on window
 *     `focus`. This is the important one: it is the moment the customer comes
 *     back from their UPI / bank app, and it produces an instant flip to
 *     "verified" instead of making them wait for the next interval tick.
 *   * Stop entirely once `stopWhen` says we are done, so a confirmed mandate
 *     does not keep generating traffic.
 *
 * The endpoint used (GET /api/caprion/mandates/status/) is a pure DB read — no
 * Digio call, no lender call, no writes — so this polling is cheap and carries no
 * billing consequence. It must NOT be pointed at
 * /api/get_verified_mandate_details/ (which can call the external VPA enrichment
 * service and write BankDetail) nor at /api/caprion/digio/verify-*-mandate/
 * (which are billed Digio calls).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';

/** Per-mandate status as reported by the backend. */
export interface MandateStatusEntry {
    /** True only when the mandate is registered (status=4 / accepted). */
    done: boolean;
    /**
     * Raw status code as stored: "1" waiting, "2" processing, "3" rejected,
     * "4" accepted, "5" unknown, "" / null when never set. Exposed so the UI can
     * tell "still pending" apart from "rejected" and offer a retry rather than an
     * endless spinner.
     */
    status: string | null;
    umrn: string | null;
    mandate_id: string | null;
}

export interface MandateStatusResponse {
    upi: MandateStatusEntry;
    nach: MandateStatusEntry;
}

/** Shape used when the server's reply is unusable — see normaliseStatus(). */
const EMPTY_ENTRY: MandateStatusEntry = {
    done: false,
    status: null,
    umrn: null,
    mandate_id: null,
};

/**
 * Coerce whatever came back into a guaranteed {upi, nach} shape.
 *
 * PRODUCTION CRASH THIS FIXES:
 *   Uncaught TypeError: Cannot read properties of undefined (reading 'done')
 *
 * The body is NOT always the JSON we expect. A 304 Not Modified carries an EMPTY
 * body, and any proxy/CDN or auth redirect in front of the app can return HTML or
 * a bare string with a 2xx. In all of those cases `res.data` is "" / undefined /
 * a string, so `data.upi.done` — evaluated inside onStatus() and stopWhen() —
 * threw and took the whole page down with it.
 *
 * Returning null for an unusable payload lets the caller skip the update entirely
 * and simply try again on the next tick, which is the correct behaviour: a missing
 * body is "no new information", not "both mandates are unregistered" (treating it
 * as the latter would wrongly un-complete a card that was already verified).
 */
function normaliseStatus(raw: unknown): MandateStatusResponse | null {
    if (!raw || typeof raw !== 'object') return null;

    const obj = raw as Record<string, unknown>;
    const entry = (value: unknown): MandateStatusEntry => {
        if (!value || typeof value !== 'object') return { ...EMPTY_ENTRY };
        const e = value as Record<string, unknown>;
        return {
            done: e.done === true,
            status: typeof e.status === 'string' ? e.status : null,
            umrn: typeof e.umrn === 'string' ? e.umrn : null,
            mandate_id: typeof e.mandate_id === 'string' ? e.mandate_id : null,
        };
    };

    // Require at least one of the two keys to be present before trusting this as a
    // status payload at all; otherwise it is some other object (an error body, an
    // HTML string parsed oddly, etc.) and must be ignored rather than read as
    // "nothing is registered".
    if (!('upi' in obj) && !('nach' in obj)) return null;

    return { upi: entry(obj.upi), nach: entry(obj.nach) };
}

interface UseMandateStatusWatcherOptions {
    /**
     * Master switch. Pass false to never poll (e.g. builder/preview mode, or a
     * card that is already finished from the initial bootstrap fetch).
     */
    enabled?: boolean;
    /** Interval between polls while the tab is visible. Default 8000 ms. */
    intervalMs?: number;
    /**
     * Return true to stop watching permanently — typically "both mandates are
     * resolved". Evaluated after every successful fetch.
     */
    stopWhen?: (status: MandateStatusResponse) => boolean;
    /**
     * Called on every successful fetch, including the first. Use this to flip
     * card state. Must be stable or cheap: it is invoked from an effect.
     */
    onStatus?: (status: MandateStatusResponse) => void;
}

export function useMandateStatusWatcher({
    enabled = true,
    intervalMs = 8000,
    stopWhen,
    onStatus,
}: UseMandateStatusWatcherOptions = {}) {
    const [status, setStatus] = useState<MandateStatusResponse | null>(null);

    // Callbacks are read through refs so that a caller passing an inline arrow
    // function does not tear down and rebuild the interval on every render.
    const onStatusRef = useRef(onStatus);
    onStatusRef.current = onStatus;
    const stopWhenRef = useRef(stopWhen);
    stopWhenRef.current = stopWhen;

    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Set once stopWhen() is satisfied. Checked before every fetch so an
    // in-flight focus handler cannot resurrect a finished watcher.
    const stoppedRef = useRef(false);
    // Guards against overlapping requests when a focus event lands on top of an
    // interval tick.
    const inFlightRef = useRef(false);
    // Survives unmount so a late-resolving request cannot setState on a dead
    // component.
    const mountedRef = useRef(true);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const fetchStatus = useCallback(async () => {
        if (stoppedRef.current || inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            const res = await axios.get<MandateStatusResponse>(
                `${API_BASE_URL}/api/caprion/mandates/status/`,
                // Never send a conditional request. Without this the browser may
                // reuse its cached ETag/Last-Modified and the server answers 304
                // with NO BODY, which is the crash described in normaliseStatus().
                // This endpoint is a cheap DB read whose whole purpose is to report
                // a value that changes underneath us, so caching it is wrong anyway.
                { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } },
            );
            if (!mountedRef.current) return;

            const data = normaliseStatus(res.data);
            // Unusable body (304/empty/HTML/error page): keep the last known good
            // status and retry on the next tick. Crucially do NOT setStatus(null)
            // — that would make a confirmed card flicker back to "pending".
            if (!data) return;

            setStatus(data);
            onStatusRef.current?.(data);

            if (stopWhenRef.current?.(data)) {
                stoppedRef.current = true;
                clearTimer();
            }
        } catch {
            // Deliberately silent. This is a background nicety, not an action the
            // user asked for: a transient failure just means the next tick (or the
            // next focus) tries again. Surfacing a toast here would spam the page
            // while the real mandate flow is working fine.
        } finally {
            inFlightRef.current = false;
        }
    }, [clearTimer]);

    useEffect(() => {
        mountedRef.current = true;

        if (!enabled) {
            clearTimer();
            return () => {
                mountedRef.current = false;
                clearTimer();
            };
        }

        // Only run the interval while the tab is actually visible: timers in a
        // hidden/frozen tab are throttled to the point of uselessness, and the
        // focus handler below is what actually matters for the return-from-UPI-app
        // case.
        const startTimer = () => {
            clearTimer();
            if (stoppedRef.current) return;
            timerRef.current = setInterval(fetchStatus, intervalMs);
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                // THE KEY MOMENT: the customer has just come back from their UPI /
                // banking app. Ask immediately so the card flips without waiting for
                // the next interval tick.
                fetchStatus();
                startTimer();
            } else {
                clearTimer();
            }
        };

        // `focus` is a belt-and-braces companion to visibilitychange: some mobile
        // browsers fire one and not the other when returning from an app switch.
        // fetchStatus() de-dupes via inFlightRef, so both firing is harmless.
        const handleFocus = () => {
            if (document.visibilityState === 'visible') fetchStatus();
        };

        // Prime immediately so the card is correct on mount (e.g. the mandate was
        // confirmed by the background sweep while the user was on another page).
        fetchStatus();
        if (document.visibilityState === 'visible') startTimer();

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', handleFocus);

        return () => {
            mountedRef.current = false;
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleFocus);
            clearTimer();
        };
    }, [enabled, intervalMs, fetchStatus, clearTimer]);

    return {
        /** Latest server-reported status, or null before the first fetch lands. */
        status,
        /** Force an out-of-band refresh (e.g. right after a Digio overlay closes). */
        refresh: fetchStatus,
    };
}

export default useMandateStatusWatcher;
